import { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';

export default function Header() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { name: 'Home', path: '/' },
    { name: 'About', path: '/about' },
    { name: 'Products', path: '/products' },
    { name: 'Partnerships', path: '/partnerships' },
    { name: 'Contact', path: '/contact' }
  ];

  const isActive = (path: string) => location.pathname === path;

  const handleNavigation = (path: string) => {
    navigate(path);
    setIsMenuOpen(false);
  };

  return (
    <header className={`fixed w-full z-50 transition-all duration-300 ${
      isScrolled ? 'bg-white shadow-md' : 'bg-transparent'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center py-4">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2">
            <h1 className={`text-2xl font-bold transition-colors ${
              isScrolled ? 'text-green-600' : 'text-white'
            }`} style={{ fontFamily: '"Pacifico", serif' }}>
              MY AVA Farms
            </h1>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            <Link 
              to="/" 
              className={`text-base font-medium transition-colors cursor-pointer whitespace-nowrap ${
                isScrolled ? 'text-gray-700 hover:text-green-600' : 'text-white hover:text-green-300'
              }`}
            >
              Home
            </Link>
            <Link 
              to="/about" 
              className={`text-base font-medium transition-colors cursor-pointer whitespace-nowrap ${
                isScrolled ? 'text-gray-700 hover:text-green-600' : 'text-white hover:text-green-300'
              }`}
            >
              About
            </Link>
            <Link 
              to="/products" 
              className={`text-base font-medium transition-colors cursor-pointer whitespace-nowrap ${
                isScrolled ? 'text-gray-700 hover:text-green-600' : 'text-white hover:text-green-300'
              }`}
            >
              Products
            </Link>
            <Link 
              to="/partnerships" 
              className={`text-base font-medium transition-colors cursor-pointer whitespace-nowrap ${
                isScrolled ? 'text-gray-700 hover:text-green-600' : 'text-white hover:text-green-300'
              }`}
            >
              Partnerships
            </Link>
            <Link 
              to="/blog" 
              className={`text-base font-medium transition-colors cursor-pointer whitespace-nowrap ${
                isScrolled ? 'text-gray-700 hover:text-green-600' : 'text-white hover:text-green-300'
              }`}
            >
              Blog
            </Link>
            <Link 
              to="/contact" 
              className={`text-base font-medium transition-colors cursor-pointer whitespace-nowrap ${
                isScrolled ? 'text-gray-700 hover:text-green-600' : 'text-white hover:text-green-300'
              }`}
            >
              Contact
            </Link>

            {/* Social Media Links */}
            <div className="flex items-center space-x-3 ml-4 pl-4 border-l border-gray-300">
              <a 
                href="https://wa.me/233233371378" 
                target="_blank" 
                rel="noopener noreferrer"
                className={`w-9 h-9 flex items-center justify-center rounded-full transition-colors cursor-pointer ${
                  isScrolled ? 'bg-green-100 text-green-600 hover:bg-green-600 hover:text-white' : 'bg-white/20 text-white hover:bg-green-600'
                }`}
                aria-label="WhatsApp"
              >
                <i className="ri-whatsapp-line text-lg"></i>
              </a>
              <a 
                href="https://web.facebook.com/profile.php?id=61583797453893" 
                target="_blank" 
                rel="noopener noreferrer"
                className={`w-9 h-9 flex items-center justify-center rounded-full transition-colors cursor-pointer ${
                  isScrolled ? 'bg-blue-100 text-blue-600 hover:bg-blue-600 hover:text-white' : 'bg-white/20 text-white hover:bg-blue-600'
                }`}
                aria-label="Facebook"
              >
                <i className="ri-facebook-fill text-lg"></i>
              </a>
            </div>
          </nav>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className={`md:hidden p-2 rounded-lg transition-colors cursor-pointer ${
              isScrolled ? 'text-gray-700 hover:bg-gray-100' : 'text-white hover:bg-white/10'
            }`}
            aria-label="Toggle menu"
          >
            <i className={`text-2xl ${isMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden py-4 bg-white shadow-lg rounded-lg mt-2">
            <nav className="flex flex-col space-y-4 px-4">
              <Link 
                to="/" 
                onClick={() => setIsMenuOpen(false)}
                className="text-base font-medium text-gray-700 hover:text-green-600 transition-colors cursor-pointer"
              >
                Home
              </Link>
              <Link 
                to="/about" 
                onClick={() => setIsMenuOpen(false)}
                className="text-base font-medium text-gray-700 hover:text-green-600 transition-colors cursor-pointer"
              >
                About
              </Link>
              <Link 
                to="/products" 
                onClick={() => setIsMenuOpen(false)}
                className="text-base font-medium text-gray-700 hover:text-green-600 transition-colors cursor-pointer"
              >
                Products
              </Link>
              <Link 
                to="/partnerships" 
                onClick={() => setIsMenuOpen(false)}
                className="text-base font-medium text-gray-700 hover:text-green-600 transition-colors cursor-pointer"
              >
                Partnerships
              </Link>
              <Link 
                to="/blog" 
                onClick={() => setIsMenuOpen(false)}
                className="text-base font-medium text-gray-700 hover:text-green-600 transition-colors cursor-pointer"
              >
                Blog
              </Link>
              <Link 
                to="/contact" 
                onClick={() => setIsMenuOpen(false)}
                className="text-base font-medium text-gray-700 hover:text-green-600 transition-colors cursor-pointer"
              >
                Contact
              </Link>

              {/* Mobile Social Links */}
              <div className="flex items-center space-x-3 pt-4 border-t border-gray-200">
                <a 
                  href="https://wa.me/233233371378" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-10 h-10 flex items-center justify-center rounded-full bg-green-100 text-green-600 hover:bg-green-600 hover:text-white transition-colors cursor-pointer"
                  aria-label="WhatsApp"
                >
                  <i className="ri-whatsapp-line text-lg"></i>
                </a>
                <a 
                  href="https://web.facebook.com/profile.php?id=61583797453893" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-10 h-10 flex items-center justify-center rounded-full bg-blue-100 text-blue-600 hover:bg-blue-600 hover:text-white transition-colors cursor-pointer"
                  aria-label="Facebook"
                >
                  <i className="ri-facebook-fill text-lg"></i>
                </a>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}
